import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { Card } from "./Card";
import { formatElapsedSec } from "@/lib/format";

/**
 * RunStatusHero — card top de qualquer página de execução longa.
 *
 * Mostra ícone de estado (working/done/failed), nome do agente, label
 * de status e tempo decorrido em destaque. Genérico pra qualquer task
 * que dispara um run em background.
 */

export type RunState = "working" | "done" | "failed" | "queued";

const STATE_META: Record<RunState, { icon: typeof CheckCircle2; bg: string; fg: string }> = {
  working: {
    icon: Loader2,
    bg: "rgba(255,112,0,0.12)",
    fg: "var(--nd-orange)",
  },
  queued: {
    icon: Loader2,
    bg: "rgba(0,0,0,0.04)",
    fg: "var(--wf-ink-3)",
  },
  done: {
    icon: CheckCircle2,
    bg: "rgba(92,184,92,0.12)",
    fg: "#3d7d3d",
  },
  failed: {
    icon: AlertTriangle,
    bg: "rgba(204,51,68,0.12)",
    fg: "var(--nd-orange-pun)",
  },
};

/** Frase de expectativa humanizada enquanto o agente trabalha. Evolui por
 *  faixa de tempo decorrido pra dar sensação de progresso e reduzir ansiedade
 *  (sem inventar dado — a atividade real vai no `subline`). Na faixa longa,
 *  lembra que dá pra sair: a notificação avisa quando terminar. */
function workingHint(elapsedSec: number): string {
  if (elapsedSec < 15) return "Começando… isso normalmente leva de 30s a 1 min ⏱️";
  if (elapsedSec < 45) return "Trabalhando nisso agora — já já fica pronto";
  if (elapsedSec < 90) return "Tarefas mais ricas levam um pouco mais. Vale a espera ✨";
  return "Ainda processando. Pode fechar essa tela — a gente te avisa quando terminar 🔔";
}

export function RunStatusHero({
  state,
  agentName,
  headline,
  subline,
  elapsedSec,
}: {
  state: RunState;
  agentName?: string | null;
  headline: string;
  subline?: string | null;
  elapsedSec: number;
}) {
  const meta = STATE_META[state];
  const IconCmp = meta.icon;
  const isWorking = state === "working" || state === "queued";
  return (
    <Card padding={20}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: meta.bg,
            color: meta.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconCmp
            size={22}
            strokeWidth={1.75}
            className={state === "working" || state === "queued" ? "wf-spin" : undefined}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {agentName && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--wf-ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 2,
              }}
            >
              {agentName}
            </div>
          )}
          <div
            className="wf-display"
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "-0.015em",
              color: "var(--wf-ink)",
              lineHeight: 1.2,
            }}
          >
            {headline}
          </div>
          {subline && (
            <div
              style={{
                fontSize: 12,
                color: "var(--wf-ink-2)",
                marginTop: 4,
                lineHeight: 1.45,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {subline}
            </div>
          )}
          {isWorking && (
            <div
              style={{
                fontSize: 12.5,
                color: "var(--wf-ink-2)",
                marginTop: 6,
                lineHeight: 1.45,
              }}
            >
              {workingHint(elapsedSec)}
            </div>
          )}
        </div>

        {/* Enquanto trabalha NÃO mostramos contador (vira "cronômetro de
            ansiedade" — a frase humanizada já dá a expectativa). Só exibimos a
            duração FINAL quando conclui/falha, como registro do que levou. */}
        {!isWorking && (
          <div
            style={{
              fontFamily: "var(--wf-font-display)",
              fontSize: 24,
              fontWeight: 700,
              color: "var(--wf-ink)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-0.01em",
            }}
          >
            {formatElapsedSec(elapsedSec)}
          </div>
        )}
      </div>
    </Card>
  );
}
